import Tesseract from 'tesseract.js';
import mammoth from 'mammoth';

// CRA typings don’t include pdfjs legacy build; use require() and treat as any.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const require: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let pdfjsLibPromise: Promise<any> | null = null;

async function getPdfJsLib(): Promise<any> {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = import('pdfjs-dist/legacy/build/pdf.mjs' as any).then((m: any) => m);
  }
  return pdfjsLibPromise;
}

export type LocalProcessProgress =
  | { stage: 'reading'; progress?: number }
  | { stage: 'ocr'; progress?: number }
  | { stage: 'pii'; progress?: number }
  | { stage: 'markdown'; progress?: number };

export interface LocalProcessResult {
  rawText: string;
  anonymizedText: string;
  markdown: string;
  ocrConfidence: number | null;
  languageUsed: string;
}

export interface NerAnonymizationOptions {
  /** Enable local NER anonymization (transformers.js). */
  enabled?: boolean;
  /** HF model id to use (default multilingual NER). */
  model?: string;
  /** HF revision (e.g. "main" or "refs/pr/2"). */
  revision?: string;
  /** dtype (e.g. "fp32"). Use fp32 to avoid *_quantized filenames. */
  dtype?: string;
  /** ONNX model file base name (e.g. "model") or full file name. */
  model_file_name?: string;
  /** Minimum confidence score [0..1] to redact an entity. Default 0.8 */
  minScore?: number;
}

const nerPipelineCache = new Map<string, Promise<any>>();

async function getNerPipeline(opts?: NerAnonymizationOptions) {
  const model = opts?.model ||  'Xenova/bert-base-multilingual-cased-ner-hrl';

  // If using the Romanian BERT PR ONNX, force settings that match the files:
  // it has `onnx/model.onnx` (not quantized).
  const effective: NerAnonymizationOptions =
    model === 'dumitrescustefan/bert-base-romanian-ner'
      ? {
          ...opts,
          revision: opts?.revision || 'refs/pr/2',
          dtype: opts?.dtype || 'fp32',
          model_file_name: opts?.model_file_name || 'model',
        }
      : opts || {};

  const key = JSON.stringify({
    model,
    revision: effective.revision || 'main',
    dtype: effective.dtype || null,
    model_file_name: effective.model_file_name || null,
  });

  const existing = nerPipelineCache.get(key);
  if (existing) return existing;

  const p = (async () => {
    const mod: any = await import('@huggingface/transformers');
    const pipeline = mod.pipeline as (task: string, model: string, options?: any) => Promise<any>;
    return await pipeline('token-classification', model, {
      revision: effective.revision || 'main',
      dtype: effective.dtype || null,
      model_file_name: effective.model_file_name || null,
      // subfolder defaults to "onnx" in @huggingface/transformers pipeline
    });
  })();

  nerPipelineCache.set(key, p);
  return p;
}

function redactSpans(text: string, spans: Array<{ start: number; end: number; repl: string }>) {
  const sorted = spans
    .filter((s) => Number.isFinite(s.start) && Number.isFinite(s.end) && s.end > s.start)
    .sort((a, b) => b.start - a.start);
  let out = text;
  for (const s of sorted) {
    out = out.slice(0, s.start) + s.repl + out.slice(s.end);
  }
  return out;
}

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

async function anonymizeWithNER(
  text: string,
  onProgress?: (p: LocalProcessProgress) => void,
  opts?: NerAnonymizationOptions
): Promise<string> {
  try {
    const minScore = typeof opts?.minScore === 'number' ? opts.minScore : 0.8;
    onProgress?.({ stage: 'pii', progress: 0.2 });
    const classifier = await getNerPipeline(opts);
    onProgress?.({ stage: 'pii', progress: 0.4 });
    // Aggregate wordpieces and return char offsets (start/end).
    const entities: any[] = await classifier(text, { aggregation_strategy: 'simple' });
    const spans: Array<{ start: number; end: number; repl: string }> = [];
    for (const e of entities || []) {
      const label = String(e?.entity_group ?? e?.entity ?? '').toUpperCase();
      const score = Number(e?.score);
      if (Number.isFinite(score) && score < minScore) continue;
      const start = Number(e?.start);
      const end = Number(e?.end);
      if (!Number.isFinite(start) || !Number.isFinite(end)) continue;

      if (label.includes('PER') || label.includes('PERSON')) spans.push({ start, end, repl: '[PERSON]' });
      else if (label.includes('LOC')) spans.push({ start, end, repl: '[LOCATION]' });
      else if (label.includes('ORG')) spans.push({ start, end, repl: '[ORG]' });
    }

    // Some models/pipeline configs return entities without start/end offsets.
    // Fallback 1: aggregated entities contain (entity_group + word) only.
    if (!spans.length && Array.isArray(entities) && entities.length) {
      const phraseSpans: Array<{ start: number; end: number; repl: string }> = [];
      let cursor = 0;
      for (const e of entities) {
        const label = String(e?.entity_group ?? e?.entity ?? '').toUpperCase();
        const word = String(e?.word ?? '').trim();
        const score = Number(e?.score);
        if (Number.isFinite(score) && score < minScore) continue;
        if (!label || !word) continue;

        // Token-level BIO handled in fallback 2 below.
        if (/^(B|I)-/.test(label)) continue;

        const repl =
          label.includes('PER') || label.includes('PERSON')
            ? '[PERSON]'
            : label.includes('LOC')
              ? '[LOCATION]'
              : label.includes('ORG')
                ? '[ORG]'
                : null;
        if (!repl) continue;

        // Match with flexible whitespace.
        const re = new RegExp(escapeRegExp(word).replace(/\s+/g, '\\s+'));
        const sub = text.slice(cursor);
        const m = sub.match(re);
        if (!m || m.index == null) continue;
        const start = cursor + m.index;
        const end = start + m[0].length;
        cursor = end;
        phraseSpans.push({ start, end, repl });
      }
      if (phraseSpans.length) return redactSpans(text, phraseSpans);
    }

    // Fallback 2: token-level BIO entities without offsets.
    // Reconstruct entity strings and locate them in the text sequentially.
    type Group = { label: string; words: string[] };
    let cur: Group | null = null;
    const groups: Group[] = [];
    if (!spans.length && Array.isArray(entities) && entities.length) {
      const toks = [...entities].sort((a, b) => Number(a?.index ?? 0) - Number(b?.index ?? 0));
   
      
     
      const flush = () => {
        if (cur && cur.words.length) groups.push(cur);
        cur = null;
      };
      for (const t of toks) {
        const ent = String(t?.entity ?? t?.entity_group ?? '').toUpperCase(); // e.g. B-PER
        const rawWord = String(t?.word ?? '').trim();
        const score = Number(t?.score);
        if (Number.isFinite(score) && score < minScore) {
          flush();
          continue;
        }
        if (!ent || !rawWord) continue;
        const m = ent.match(/^(B|I)-(.+)$/);
        if (!m) continue;
        const bi = m[1];
        const typ = m[2];
        const isTarget = typ.includes('PER') || typ.includes('PERSON') || typ.includes('LOC') || typ.includes('ORG');
        if (!isTarget) {
          flush();
          continue;
        }
        if (!cur || bi === 'B' || cur.label !== typ) {
          flush();
          cur = { label: typ, words: [] };
        }
        cur.words.push(rawWord);
      }
      flush();

      const findSpans: Array<{ start: number; end: number; repl: string }> = [];
      let cursor = 0;
      for (const g of groups) {
        // Join wordpieces: "Mer" + "##cea" => "Mercea"
        let phrase = '';
        for (const w of g.words) {
          if (w.startsWith('##')) phrase += w.slice(2);
          else phrase += (phrase ? ' ' : '') + w;
        }
        phrase = phrase.trim();
        if (!phrase) continue;

        const re = new RegExp(escapeRegExp(phrase).replace(/\s+/g, '\\s+'));
        const sub = text.slice(cursor);
        const m = sub.match(re);
        if (!m || m.index == null) continue;
        const idx = cursor + m.index;
        const end = idx + m[0].length;
        cursor = end;
        const repl =
          g.label.includes('PER') || g.label.includes('PERSON')
            ? '[PERSON]'
            : g.label.includes('LOC')
              ? '[LOCATION]'
              : '[ORG]';
        findSpans.push({ start: idx, end, repl });
      }
      if (findSpans.length) return redactSpans(text, findSpans);
    }

    return spans.length ? redactSpans(text, spans) : text;
  } catch(e) {
    console.error('anonymizeWithNER error', e);
    return text;
  }
}

function clamp01(n: number) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

async function fileToDataUrl(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('Failed reading file'));
    r.onload = () => resolve(String(r.result || ''));
    r.readAsDataURL(file);
  });
}

function extOf(filename: string): string {
  const m = filename.toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
}

function isProbablyTextMime(mime: string) {
  return mime.startsWith('text/');
}

function stripRtfToText(rtf: string): string {
  // Very lightweight RTF → plain text. Good enough for typical medical exports.
  let s = rtf;
  s = s.replace(/\r\n/g, '\n');
  s = s.replace(/\{\\\*\\[^}]+\}/g, ''); // remove RTF destinations
  s = s.replace(/\\par[d]?/g, '\n');
  s = s.replace(/\\tab/g, '\t');
  s = s.replace(/\\'[0-9a-fA-F]{2}/g, ''); // ignore hex escapes (keeps it simple)
  s = s.replace(/\\[a-zA-Z]+\d* ?/g, '');
  s = s.replace(/[{}]/g, '');
  s = s.replace(/\n{3,}/g, '\n\n');
  return s.trim();
}

function normalizeText(s: string): string {
  let out = s || '';
  // Normalize Unicode (fix ligatures / mixed forms).
  try {
    out = out.normalize('NFKC');
  } catch {
    // ignore
  }
  // Replace common invisible / odd spacing chars.
  out = out
    .replace(/\u00A0/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\r\n/g, '\n');

  // Drop most control chars, keep \n and \t.
  out = Array.from(out)
    .filter((ch) => {
      const c = ch.charCodeAt(0);
      if (ch === '\n' || ch === '\t') return true;
      if (c === 0x7f) return false;
      if (c >= 0x00 && c <= 0x1f) return false;
      return true;
    })
    .join('');

  // Collapse excessive whitespace.
  out = out.replace(/[ \t]{3,}/g, '  ').replace(/\n{3,}/g, '\n\n');
  return out.trim();
}

function looksLikeGoodPdfText(s: string): boolean {
  const t = normalizeText(s);
  if (t.length < 80) return false;
  const repl = (t.match(/\uFFFD/g) || []).length;
  const weird = (t.match(/[�]/g) || []).length;
  const total = Math.max(1, t.length);
  const badRatio = (repl + weird) / total;
  // If too many replacement chars, likely garbage extraction.
  if (badRatio > 0.002) return false;
  // Heuristic: needs some word-like tokens.
  const words = (t.match(/\b[A-Za-zĂÂÎȘȚăâîșț]{2,}\b/g) || []).length;
  return words >= 10;
}

export function anonymizePII(input: string): string {
  let s = input;

  const PERSON = '[PERSON]';

  const capWord = '[A-ZĂÂÎȘȚ][a-zăâîșț-]+';
  const nameChunk = `(?:${capWord})(?:\\s+${capWord}){0,3}`;
  const lowerWord = '[a-zăâîșț][a-zăâîșț-]+';
  const looseNameChunk = `(?:${capWord}|${lowerWord})(?:\\s+(?:${capWord}|${lowerWord})){0,3}`;

  function redactAfterLabel(labelRe: RegExp) {
    // Redact "Label: <Name ...>" patterns on the same line.
    s = s.replace(labelRe, (_m, p1, p2) => `${p1}${PERSON}${p2 ?? ''}`);
  }

  // Names (conservative: only after explicit labels / honorifics)
  // Examples: "Nume: Popescu", "Prenume: Ion", "Pacient: Popescu Ion"
  redactAfterLabel(new RegExp(`(^|\\n)(\\s*(?:Nume|Prenume|Nume\\s+și\\s+prenume|Pacient(?:ul)?|Beneficiar(?:ul)?|Asigurat(?:ul)?|Titular(?:ul)?|Persoan[ăa])\\s*[:=-]\\s*)${nameChunk}(\\b)?`, 'gi'));
  // Inline "Dl/Dna/Doamna/Domn" + Name Surname
  s = s.replace(
    new RegExp(`\\b(Dl\\.?|Dna\\.?|Domnul|Doamna)\\s+${nameChunk}\\b`, 'gi'),
    `$1 ${PERSON}`
  );
  // "Dr." (often clinician name; still PII in documents)
  s = s.replace(
    new RegExp(`\\b(Dr\\.?|Doctor)\\s+${nameChunk}\\b`, 'gi'),
    `$1 ${PERSON}`
  );
  // Signature lines: "Semnătura: Popescu Ion"
  redactAfterLabel(new RegExp(`(^|\\n)(\\s*(?:Semn[ăa]tur[ăa]|Semnat|Semnatar|Paraf[ăa])\\s*[:=-]\\s*)${nameChunk}(\\b)?`, 'gi'));

  // Email forwards often contain headers like:
  // "From: straciuc oreste <...>" or "From: Mercea Emilia (...)"
  // Keep it conservative: only after "From:" at line start.
  redactAfterLabel(new RegExp(`(^|\\n)(\\s*From\\s*[:=-]\\s*)${looseNameChunk}(\\b)?`, 'gi'));

  // Emails
  s = s.replace(
    /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
    '[EMAIL]'
  );

  // Phone numbers (RO-ish, permissive)
  s = s.replace(
    /\b(?:\+?\d{1,3}[\s.-]?)?(?:\(?\d{2,3}\)?[\s.-]?)?\d{3}[\s.-]?\d{3,4}\b/g,
    (m) => (m.replace(/\D/g, '').length >= 9 ? '[PHONE]' : m)
  );

  // CNP (13 digits)
  s = s.replace(/\b\d{13}\b/g, '[CNP]');

  // IBAN (RO + 22 chars total; allow spaces)
  s = s.replace(/\bRO\d{2}(?:\s?[A-Z0-9]{4}){5}\s?[A-Z0-9]{2}\b/gi, '[IBAN]');

  // Dates (dd.mm.yyyy, dd/mm/yyyy)
  s = s.replace(/\b([0-3]?\d)[./-]([01]?\d)[./-]((?:19|20)\d{2})\b/g, '[DATE]');

  // IDs (series/number patterns) - conservative
  s = s.replace(/\b([A-Z]{2})\s?\d{6}\b/g, '[ID]');

  return s;
}

export function textToMarkdown(anonymizedText: string): string {
  const cleaned = anonymizedText
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  if (!cleaned) return '# Document (OCR)\n\n_(no text detected)_\n';

  // Keep original line breaks; add a lightweight header.
  return `# Document (OCR)\n\n${cleaned}\n`;
}

function setupPdfJsWorker(pdfjsLib: any) {
  // Bundle the worker locally (no CDN fetch).
  // Use legacy build + require() to let CRA emit the worker as an asset.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (pdfjsLib as any).GlobalWorkerOptions.workerSrc = require('pdfjs-dist/legacy/build/pdf.worker.min.mjs');
  } catch {
    // If this fails in an older bundler, pdf.js will try its defaults.
  }
}

async function extractPdfText(
  file: File,
  onProgress?: (p: LocalProcessProgress) => void
): Promise<string> {
  const pdfjsLib = await getPdfJsLib();
  setupPdfJsWorker(pdfjsLib);
  const ab = await file.arrayBuffer();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const loadingTask: any = (pdfjsLib as any).getDocument({ data: ab });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pdf: any = await loadingTask.promise;
  const numPages: number = pdf.numPages || 0;
  const pages: string[] = [];

  for (let pageNum = 1; pageNum <= numPages; pageNum++) {
    onProgress?.({ stage: 'reading', progress: (pageNum - 1) / Math.max(1, numPages) });
    const page = await pdf.getPage(pageNum);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const content: any = await page.getTextContent();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const items: any[] = Array.isArray(content?.items) ? content.items : [];

    // Group items by approximate Y to rebuild lines.
    const lines = new Map<number, string[]>();
    for (const it of items) {
      const str = String(it?.str ?? '').trimEnd();
      if (!str) continue;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tr: any = it?.transform;
      const y = typeof tr?.[5] === 'number' ? tr[5] : 0;
      const yKey = Math.round(y / 2) * 2; // tolerance bucket
      const arr = lines.get(yKey) || [];
      arr.push(str);
      lines.set(yKey, arr);
    }

    const sorted = Array.from(lines.entries()).sort((a, b) => b[0] - a[0]); // top to bottom
    const pageText = sorted
      .map(([, parts]) => parts.join(' ').replace(/[ \t]{2,}/g, ' ').trim())
      .filter(Boolean)
      .join('\n');

    pages.push(`## Page ${pageNum}\n\n${pageText}`.trim());
  }

  onProgress?.({ stage: 'reading', progress: 1 });
  return pages.join('\n\n');
}

async function tryRecognizeWithLang(
  dataUrl: string,
  lang: string,
  onProgress?: (p: LocalProcessProgress) => void
): Promise<{ text: string; confidence: number | null; languageUsed: string }> {
  const { data } = await Tesseract.recognize(dataUrl, lang, {
    logger: (m) => {
      if (m?.status === 'recognizing text') {
        onProgress?.({ stage: 'ocr', progress: clamp01(Number(m.progress)) });
      }
    },
  });

  const t = String(data?.text || '').trim();
  const conf = typeof data?.confidence === 'number' ? data.confidence : null;
  return { text: t, confidence: conf, languageUsed: lang };
}

async function recognizeImageDataUrl(
  dataUrl: string,
  onProgress?: (p: LocalProcessProgress) => void
): Promise<{ text: string; confidence: number | null; languageUsed: string }> {
  try {
    return await tryRecognizeWithLang(dataUrl, 'ron+eng', onProgress);
  } catch {
    return await tryRecognizeWithLang(dataUrl, 'eng', onProgress);
  }
}

async function extractDocxText(file: File): Promise<string> {
  const ab = await file.arrayBuffer();
  const res = await mammoth.extractRawText({ arrayBuffer: ab });
  return String(res?.value || '').trim();
}

async function extractTxtLikeText(file: File): Promise<string> {
  const t = await file.text();
  return String(t || '').replace(/\r\n/g, '\n').trim();
}

async function ocrPdf(file: File, onProgress?: (p: LocalProcessProgress) => void) {
  const pdfjsLib = await getPdfJsLib();
  setupPdfJsWorker(pdfjsLib);
  const ab = await file.arrayBuffer();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const loadingTask: any = (pdfjsLib as any).getDocument({ data: ab });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pdf: any = await loadingTask.promise;
  const numPages: number = pdf.numPages || 0;
  const texts: string[] = [];
  const confidences: number[] = [];

  for (let pageNum = 1; pageNum <= numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 2.5 });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);

    await page.render({ canvasContext: ctx, viewport }).promise;

    // Improve OCR on scanned PDFs: high-contrast grayscale threshold.
    try {
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = img.data;
      for (let i = 0; i < d.length; i += 4) {
        const r = d[i], g = d[i + 1], b = d[i + 2];
        // Luma
        const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        const v = y > 190 ? 255 : 0;
        d[i] = d[i + 1] = d[i + 2] = v;
      }
      ctx.putImageData(img, 0, 0);
    } catch {
      // ignore; keep original render
    }
    const dataUrl = canvas.toDataURL('image/png');

    const res = await recognizeImageDataUrl(dataUrl, (p) => {
      if (p.stage === 'ocr') {
        const pageProgress = clamp01(p.progress ?? 0);
        const overall = (pageNum - 1 + pageProgress) / Math.max(1, numPages);
        onProgress?.({ stage: 'ocr', progress: overall });
      } else {
        onProgress?.(p);
      }
    });

    texts.push(`## Page ${pageNum}\n\n${res.text || ''}`.trim());
    if (typeof res.confidence === 'number') confidences.push(res.confidence);
  }

  const avgConf =
    confidences.length ? confidences.reduce((a, b) => a + b, 0) / confidences.length : null;

  return {
    text: texts.join('\n\n'),
    confidence: avgConf,
    languageUsed: 'ron+eng',
  };
}

export async function processDocumentLocally(
  file: File,
  onProgress?: (p: LocalProcessProgress) => void,
  options?: { ner?: NerAnonymizationOptions }
): Promise<LocalProcessResult> {
  onProgress?.({ stage: 'reading', progress: 0 });
  const mime = (file.type || '').toLowerCase();
  const ext = extOf(file.name);

  // Prefer extraction when we can (docx/txt/rtf), OCR for images/PDF.
  let rawText = '';
  let confidence: number | null = null;
  let languageUsed = 'eng';

  if (
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    ext === 'docx'
  ) {
    rawText = await extractDocxText(file);
    languageUsed = 'extracted';
    onProgress?.({ stage: 'reading', progress: 1 });
  } else if (isProbablyTextMime(mime) || ext === 'txt') {
    rawText = await extractTxtLikeText(file);
    languageUsed = 'extracted';
    onProgress?.({ stage: 'reading', progress: 1 });
  } else if (mime === 'application/rtf' || ext === 'rtf') {
    const rtf = await extractTxtLikeText(file);
    rawText = stripRtfToText(rtf);
    languageUsed = 'extracted';
    onProgress?.({ stage: 'reading', progress: 1 });
  } else if (mime === 'application/pdf' || ext === 'pdf') {
    // Prefer native text extraction (avoids OCR artifacts) when it looks sane.
    const extracted = await extractPdfText(file, onProgress);
    if (looksLikeGoodPdfText(extracted)) {
      rawText = extracted;
      confidence = null;
      languageUsed = 'pdf-text';
    } else {
      const res = await ocrPdf(file, onProgress);
      rawText = res.text;
      confidence = res.confidence;
      languageUsed = res.languageUsed;
    }
  } else {
    const dataUrl = await fileToDataUrl(file);
    onProgress?.({ stage: 'reading', progress: 1 });
    const res = await recognizeImageDataUrl(dataUrl, onProgress);
    rawText = res.text;
    confidence = res.confidence;
    languageUsed = res.languageUsed;
  }

  rawText = normalizeText(rawText);

  onProgress?.({ stage: 'pii', progress: 0 });
  let working = rawText;
  if (options?.ner?.enabled) {
    working = await anonymizeWithNER(working, onProgress, options.ner);
  }
  const anonymized = anonymizePII(working);
  onProgress?.({ stage: 'pii', progress: 1 });

  onProgress?.({ stage: 'markdown', progress: 0 });
  const markdown = textToMarkdown(anonymized);
  onProgress?.({ stage: 'markdown', progress: 1 });

  return {
    rawText,
    anonymizedText: anonymized,
    markdown,
    ocrConfidence: confidence,
    languageUsed,
  };
}

